import { useState, useEffect, useRef, useCallback } from 'react';
import { Common, Widget, color } from '@stockbit/ui';
import { useDispatch, useSelector } from 'react-redux';

import slice, { effects, selectors, symbolSelector } from './slice';
import { HeaderContainer, Input, Tag, SymbolSearch, TagDesc } from './styled';
import { useRouter } from 'next/router';

const { AutoSuggestion } = Widget;
const { Text, Flex } = Common;

const { actions } = slice;

function Header() {
  const router = useRouter();
  const dispatch = useDispatch();
  const { isSearchbarActive, name, searchResult } =
    useSelector(selectors);

  const symbol = useSelector(symbolSelector);

  const intervalRef = useRef<NodeJS.Timeout>();
  const [visible, setVisible] = useState<boolean>(false);

  const options = searchResult.map(({ name: companyName, desc }) => ({
    value: companyName,
    label: (
      <Flex>
        <Tag>{companyName}</Tag>
        <TagDesc>{desc}</TagDesc>
      </Flex>
    ),
  }));

  const inputContainerRef = useRef(null);
  const inputRef = useRef(null);

  const handleOutsideClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      if (
        inputContainerRef.current &&
        !inputContainerRef.current?.contains(e.target)
      ) {
        dispatch(actions.setisSearchbarActive({ isOpen: false }));
        setVisible(false);
      }
    },
    [dispatch],
  );

  const onSymbolSearchClick = (e: MouseEvent) => {
    e.stopPropagation();
    dispatch(actions.setisSearchbarActive({ isOpen: true }));
    setVisible(true);
    inputRef.current.focus();
  };

  const handleSearchCompany = (keyword: string) => {
    if (keyword.length > 1) {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }

      intervalRef.current = setTimeout(() => {
        dispatch(effects.fetchSymbolSuggestions({ keyword }));
      }, 1500);
    }
  };

  const handleSuggestionClick = (selectedSymbol: string) => {
    router.push(`/symbol/${selectedSymbol}`);
    dispatch(actions.setSymbol({ symbol: selectedSymbol }));
  };

  const handlePressEnter = async (e) => {
    let val = e;

    if (typeof e !== 'string') {
      val = e.target.value;
    }

    if (val.length > 1) {
      const search = await dispatch(
        effects.fetchSymbolSuggestions({ keyword: val }),
      );

      // @ts-ignore
      const { company } = search.payload;

      if (company && company.length > 0) {
        const [selected] = company;

        handleSuggestionClick(selected.name);
      }
    }
  };

  const handleChangeSymbol = (e) => {
    e?.stopPropagation();
    dispatch(actions.setisSearchbarActive({ isOpen: false }));
    setVisible(false);
  };

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [handleOutsideClick]);

  return (
    <HeaderContainer>
      <div ref={inputContainerRef}>
        <AutoSuggestion
          options={options}
          style={{
            position: 'absolute',
            bottom: '0',
            width: 'calc(100% - 20px)',
          }}
          onSearch={handleSearchCompany}
          onSelect={handleSuggestionClick}
          open={visible}
          getPopupContainer={(trigger) => trigger.parentElement}
        >
          <Input
            ref={inputRef}
            active={isSearchbarActive}
            placeholder="Type stock here"
            width={isSearchbarActive ? '100%' : '0px'}
            onPressEnter={handlePressEnter}
            suffix={
              isSearchbarActive && (
                <Text
                  size="12px"
                  color={color.midgray}
                  style={{ cursor: 'pointer' }}
                  onClick={handleChangeSymbol}
                >
                  change symbol
                </Text>
              )
            }
          />
        </AutoSuggestion>
        <SymbolSearch onClick={onSymbolSearchClick} active={!isSearchbarActive}>
          {symbol}
        </SymbolSearch>
      </div>
      <Text
        style={{ display: isSearchbarActive ? 'none' : 'block' }}
        size="12px"
        ellipsis
      >
        {name}
      </Text>
    </HeaderContainer>
  );
}

export default Header;
